// A plan's economics (PE7–PE9) and each person's load across projects (PE6, §3.9). Pure, and run
// in the browser on every grid edit as well as in the API for lists and saves.

import { addDays, addMonths, daysBetween, eachMonth, monthOf, type Iso, type Month } from "./dates";
import { buildCalendar, costPlan, rateOn, weekCapacity, type Calendar, type RateStep } from "./engine";
import { priceForTarget } from "./margin";
import type { Project, PlanExpense, WorkingPlan } from "./projects";
import { cashByMonth, contractValue, expandTiers, recognizedByMonth } from "./revenue";
import { countsTowardCapacity, type ProjectStatus } from "./status";

/**
 * Base, OTE, or Target and Target OTE: each role at its target rate, whoever fills it (§3.12,
 * rev. 1.16; Target OTE adds the target's bonus, Chris 2026-10-15).
 */
export type CostBasis = "base" | "ote" | "target" | "targetOte";
export const isTargetBasis = (b: CostBasis): b is "target" | "targetOte" => b === "target" || b === "targetOte";

export interface RowRates {
  base: RateStep[];
  ote: RateStep[];
  /** Roles only: the target rates (§3.12). A person is costed at the target of the role they fill. */
  target?: RateStep[];
  targetOte?: RateStep[];
}

/** Rate steps a plan is costed with, as the API sends them. */
export interface CostRates {
  /** Per-person Base and OTE steps. Present only for callers with comp.read (SEC3). */
  people?: Record<string, RowRates>;
  /** Role standard rate steps: placeholders, and everyone for callers without comp.read. */
  roles: Record<string, RowRates & { unrated: boolean }>;
}

/** The rates for each assignment: the person's own when visible, otherwise their role's standard rate. */
/** Target steps where a target is in effect, and the Base steps before it starts (§3.12). */
export function targetOrBase(target: readonly RateStep[] | undefined, base: RateStep[]): RateStep[] {
  const first = target?.find((t) => t.rate > 0)?.from;
  if (!target || !first) return base;
  return [...base.filter((b) => b.from < first), ...target.filter((t) => t.from >= first)];
}

export function ratesForPlan(plan: Pick<WorkingPlan, "assignments">, rates: CostRates): (RowRates | undefined)[] {
  return plan.assignments.map((a) => {
    const role = rates.roles[a.roleId];
    const own = a.personId ? rates.people?.[a.personId] : undefined;
    const row = own ?? role;
    if (!row) return undefined;
    // Under Target the row takes its role's target; before the target starts, or with none, Base (§3.12).
    return { ...row, target: targetOrBase(role?.target, row.base), targetOte: targetOrBase(role?.targetOte, row.ote) };
  });
}

export interface EstimateOptions {
  basis: CostBasis;
  targetMargin: number;
  /** Fully burdened view: overhead charged per planned hour. Zero for contribution only. */
  overheadPerHour: number;
  holidays: ReadonlySet<Iso>;
}

export interface MonthEconomics {
  month: Month;
  hours: number;
  labor: number;
  /** Non-labor costs that are not billed back. */
  expenses: number;
  /** Recognized revenue, excluding reimbursables. */
  revenue: number;
  margin: number;
  marginPct: number | null;
  cashIn: number;
  cashOut: number;
  cumulativeCash: number;
}

export interface Estimate {
  calendar: Calendar;
  hours: number;
  labor: number;
  expenses: number;
  reimbursable: number;
  /** Total recognized revenue excluding reimbursables: what margin is measured against. */
  revenue: number;
  /** Client fees: the price. */
  clientFees: number;
  /** Partner funding and referral revenue. */
  otherRevenue: number;
  contribution: number;
  contributionPct: number | null;
  overhead: number;
  burdened: number;
  burdenedPct: number | null;
  targetMargin: number;
  /** The client fees that would earn exactly the target margin, given the other revenue. */
  priceForTarget: number;
  rowHours: Float64Array;
  rowCost: Float64Array;
  weekHours: Float64Array;
  weekCost: Float64Array;
  months: MonthEconomics[];
}

export function expenseMonths(e: PlanExpense): Month[] {
  return Array.from({ length: e.count }, (_, i) => addMonths(e.firstMonth, i));
}

/** The holidays a plan's weeks need: entered lists, plus the standard list for years nobody entered. */
export function planCalendar(plan: Pick<WorkingPlan, "startMonday" | "weeks">, holidays: ReadonlySet<Iso>): Calendar {
  return buildCalendar(plan.startMonday, plan.weeks, holidays);
}

/**
 * Economics of a plan. `rates[i]` costs assignment i; a missing entry costs $0 and should have been
 * reported to the user by whoever built the rates.
 */
export function estimatePlan(plan: WorkingPlan, rates: readonly (RowRates | undefined)[], o: EstimateOptions): Estimate {
  const calendar = planCalendar(plan, o.holidays);
  const rows = plan.assignments.map((a, i) => ({ person: a.personId, rates: rates[i]?.[o.basis] ?? [] }));
  const hours = plan.assignments.map((a) => Float64Array.from(a.hours));
  const cost = costPlan(calendar, rows, hours);

  const lines = expandTiers(plan.schedule);
  const billed = lines.filter((l) => l.type !== "reimbursable");
  const recognized = recognizedByMonth(billed);
  const cash = cashByMonth(billed);
  const clientFees = contractValue(lines.filter((l) => l.type === "client-fee"));
  const otherRevenue = contractValue(billed) - clientFees;

  const expenseByMonth = new Map<Month, number>();
  const reimbursableByMonth = new Map<Month, number>();
  for (const e of plan.expenses) {
    const into = e.reimbursable ? reimbursableByMonth : expenseByMonth;
    for (const m of expenseMonths(e)) into.set(m, (into.get(m) ?? 0) + e.amount);
  }
  const sum = (m: Map<Month, number>) => [...m.values()].reduce((a, b) => a + b, 0);
  const expenses = sum(expenseByMonth);
  const reimbursable = sum(reimbursableByMonth);
  const revenue = sum(recognized);

  // Every month anything happens in, from the earliest to the latest.
  const touched = [...cost.monthHours.keys(), ...recognized.keys(), ...cash.keys(), ...expenseByMonth.keys(), ...reimbursableByMonth.keys()].sort();
  const months: MonthEconomics[] = [];
  if (touched.length) {
    let running = 0;
    for (const month of eachMonth(`${touched[0]}-01`, `${touched.at(-1)}-01`)) {
      const labor = cost.monthCost.get(month) ?? 0;
      const exp = expenseByMonth.get(month) ?? 0;
      const reimb = reimbursableByMonth.get(month) ?? 0;
      const rev = recognized.get(month) ?? 0;
      const cashIn = (cash.get(month) ?? 0) + reimb; // reimbursables are billed when incurred
      const cashOut = labor + exp + reimb;
      running += cashIn - cashOut;
      const margin = rev - labor - exp;
      months.push({
        month,
        hours: cost.monthHours.get(month) ?? 0,
        labor,
        expenses: exp,
        revenue: rev,
        margin,
        marginPct: rev ? margin / rev : null,
        cashIn,
        cashOut,
        cumulativeCash: running,
      });
    }
  }

  const contribution = revenue - cost.totalCost - expenses;
  const overhead = o.overheadPerHour * cost.totalHours;
  const burdened = contribution - overhead;
  const pct = (x: number) => (revenue ? x / revenue : null);
  return {
    calendar,
    hours: cost.totalHours,
    labor: cost.totalCost,
    expenses,
    reimbursable,
    revenue,
    clientFees,
    otherRevenue,
    contribution,
    contributionPct: pct(contribution),
    overhead,
    burdened,
    burdenedPct: pct(burdened),
    targetMargin: o.targetMargin,
    priceForTarget: Math.max(0, priceForTarget(cost.totalCost + expenses, o.targetMargin) - otherRevenue),
    rowHours: cost.rowHours,
    rowCost: cost.rowCost,
    weekHours: cost.weekHours,
    weekCost: cost.weekCost,
    months,
  };
}

// --- Zoom (PE4) ----------------------------------------------------------------------------------

export type Zoom = "week" | "month" | "quarter";

export interface Period {
  key: string;
  label: string;
  /** Plan week indexes in this period. A week belongs to the month of its Thursday, where most of its working days fall. */
  weeks: number[];
}

export function periodsOf(plan: Pick<WorkingPlan, "startMonday" | "weeks">, zoom: Zoom): Period[] {
  const out: Period[] = [];
  for (let w = 0; w < plan.weeks; w++) {
    const monday = addDays(plan.startMonday, w * 7);
    const month = monthOf(addDays(monday, 3));
    let key: string;
    let label: string;
    if (zoom === "week") {
      key = monday;
      label = monday.slice(5); // MM-DD, under a YYYY-MM group heading
    } else if (zoom === "month") {
      key = month;
      label = month; // YYYY-MM (Chris, 2026-10-14)
    } else {
      const q = Math.floor((Number(month.slice(5)) - 1) / 3) + 1;
      key = `${month.slice(0, 4)}-Q${q}`;
      label = key;
    }
    const last = out.at(-1);
    if (last?.key === key) last.weeks.push(w);
    else out.push({ key, label, weeks: [w] });
  }
  return out;
}

// --- Load (PE6, §3.9) ----------------------------------------------------------------------------

export interface PersonWeeks {
  /** Hours the person is planned on other projects, per plan week. */
  other: number[];
  /** Capacity per plan week: hours a week × working days ÷ 5, and zero outside their employment. */
  capacity: number[];
  /** What makes up `other`, for the hover. */
  contributors: { projectId: string; code: string; name: string; hours: number[] }[];
}

/** Load per week for one person: this plan's hours plus `other`, over capacity. */
export function personLoad(thisPlan: readonly number[], weeks: PersonWeeks): number[] {
  return thisPlan.map((h, w) => {
    const total = h + (weeks.other[w] ?? 0);
    const cap = weeks.capacity[w] ?? 0;
    return cap > 0 ? total / cap : total > 0 ? Infinity : 0;
  });
}

/** Capacity for each week of a calendar, given hours a week in effect on each Monday (0 when not employed). */
export function capacityWeeks(cal: Calendar, hoursPerWeekOn: (monday: Iso) => number): number[] {
  return cal.weekStarts.map((monday, w) => weekCapacity(hoursPerWeekOn(monday), cal.weekDays[w].length));
}


/**
 * Capacity less approved time off (EC12, rev. 1.16): the exact hours booked on each working day of a
 * week come off that week. Holidays are already out of the calendar's working days.
 */
export function lessBookedOff(cal: Calendar, capacity: readonly number[], booked: ReadonlyMap<Iso, number> | undefined): number[] {
  if (!booked?.size) return [...capacity];
  return capacity.map((c, w) => Math.max(0, c - cal.weekDays[w].reduce((t, d) => t + (booked.get(d) ?? 0), 0)));
}

/** PRD §3.10 defaults: pending and booked count toward capacity; prospects and opportunities are optional (CM3, P1d). */
export const DEFAULT_LOAD_STATUSES: ReadonlySet<ProjectStatus> = new Set(["pending", "booked"]);

/**
 * Hours each person is planned on every other counted project, aligned to `cal`'s weeks. Templates
 * never count. A cancelled project counts only before its cancellation date, so its future weeks are
 * released at once (§3.10).
 */
export function otherProjectHours(
  projects: readonly Pick<Project, "id" | "code" | "name" | "status" | "closedOn" | "isTemplate" | "plan">[],
  excludeId: string | null,
  cal: Calendar,
  included: ReadonlySet<ProjectStatus> = DEFAULT_LOAD_STATUSES,
): Map<string, { other: number[]; contributors: PersonWeeks["contributors"] }> {
  const weeks = cal.weekStarts.length;
  const start = cal.weekStarts[0];
  const out = new Map<string, { other: number[]; contributors: PersonWeeks["contributors"] }>();
  for (const p of projects) {
    if (p.id === excludeId || p.isTemplate) continue;
    const offset = Math.round(daysBetween(start, p.plan.startMonday) / 7);
    for (const a of p.plan.assignments) {
      if (!a.personId) continue;
      let entry = out.get(a.personId);
      let mine: number[] | undefined;
      a.hours.forEach((h, w) => {
        const idx = w + offset;
        if (!h || idx < 0 || idx >= weeks) return;
        if (!countsTowardCapacity(p, addDays(p.plan.startMonday, w * 7), included)) return;
        if (!entry) out.set(a.personId!, (entry = { other: new Array(weeks).fill(0), contributors: [] }));
        if (!mine) {
          const existing = entry.contributors.find((c) => c.projectId === p.id);
          mine = existing?.hours ?? new Array(weeks).fill(0);
          if (!existing) entry.contributors.push({ projectId: p.id, code: p.code, name: p.name, hours: mine });
        }
        entry.other[idx] += h;
        mine[idx] += h;
      });
    }
  }
  return out;
}

export interface CoverageGap {
  personId: string;
  /** Weeks with hours where the person has no pay on file: those hours cost $0. */
  noPayWeeks: number;
  /** Weeks with hours where the person has no capacity on file: load can't be measured. */
  noCapacityWeeks: number;
  /** When their pay starts, if it does within the rates given. */
  firstPay?: Iso;
}

/**
 * Named people planned in weeks they have no pay or capacity on file for: usually a start date
 * entered later than the work. Pay is checked mid-week, so a raise or start on a Wednesday counts
 * from that week. Pass only the inputs the caller has: no person rates, no pay check.
 */
export function coverageGaps(
  plan: Pick<WorkingPlan, "startMonday" | "weeks" | "assignments">,
  personRates?: Record<string, RowRates>,
  capacity?: Record<string, { capacity: number[] }>,
): CoverageGap[] {
  const hoursByPerson = new Map<string, number[]>();
  for (const a of plan.assignments) {
    if (!a.personId) continue;
    const sum = hoursByPerson.get(a.personId) ?? new Array(plan.weeks).fill(0);
    a.hours.forEach((h, w) => (sum[w] += h));
    hoursByPerson.set(a.personId, sum);
  }
  const out: CoverageGap[] = [];
  for (const [personId, hours] of hoursByPerson) {
    const steps = personRates?.[personId]?.base;
    const cap = capacity?.[personId]?.capacity;
    let noPayWeeks = 0;
    let noCapacityWeeks = 0;
    hours.forEach((h, w) => {
      if (!h) return;
      if (steps && rateOn(steps, addDays(plan.startMonday, w * 7 + 2)) === 0) noPayWeeks++;
      if (cap && !cap[w]) noCapacityWeeks++;
    });
    if (noPayWeeks || noCapacityWeeks) out.push({ personId, noPayWeeks, noCapacityWeeks, firstPay: steps?.find((s) => s.rate > 0)?.from });
  }
  return out;
}
